<script setup lang="ts">
import { computed, ref } from 'vue';
import { useFlowStore } from '@/stores/flow';
import ErrorBanner from './ErrorBanner.vue';

const flow = useFlowStore();
const fName = ref('');
const lName = ref('');
const emailAddress = ref('');
const userName = ref('');
const password = ref('');
const confirmPassword = ref('');
const touched = ref(false);

const passwordMismatch = computed(
  () =>
    touched.value &&
    password.value.length > 0 &&
    confirmPassword.value.length > 0 &&
    password.value !== confirmPassword.value,
);

const canSubmit = computed(() => {
  return (
    fName.value.trim() &&
    lName.value.trim() &&
    emailAddress.value.trim() &&
    userName.value.trim() &&
    password.value.length >= 8 &&
    password.value === confirmPassword.value
  );
});

async function submit() {
  touched.value = true;
  if (!canSubmit.value) return;
  await flow.signUp({
    fName: fName.value.trim(),
    lName: lName.value.trim(),
    emailAddress: emailAddress.value.trim(),
    userName: userName.value.trim(),
    password: password.value,
    studentRole: true,
  });
}
</script>

<template>
  <section class="screen">
    <h1 class="title">Create your account</h1>
    <p class="lede">
      Tell us who you are. Your certificate, when you finish the course, will
      use this name.
    </p>

    <ErrorBanner
      v-if="flow.error"
      :message="flow.error"
      @dismiss="flow.clearError()"
    />

    <form class="form" @submit.prevent="submit">
      <div class="row-2">
        <label class="field">
          <span class="label-text">First name</span>
          <input v-model="fName" type="text" autocomplete="given-name" :disabled="flow.busy" class="text-input" />
        </label>
        <label class="field">
          <span class="label-text">Last name</span>
          <input v-model="lName" type="text" autocomplete="family-name" :disabled="flow.busy" class="text-input" />
        </label>
      </div>

      <label class="field">
        <span class="label-text">Email</span>
        <input
          v-model="emailAddress"
          type="email"
          autocomplete="email"
          autocapitalize="none"
          spellcheck="false"
          :disabled="flow.busy"
          class="text-input"
        />
      </label>

      <label class="field">
        <span class="label-text">Username</span>
        <input
          v-model="userName"
          type="text"
          autocomplete="username"
          autocapitalize="none"
          spellcheck="false"
          :disabled="flow.busy"
          class="text-input"
        />
      </label>

      <div class="row-2">
        <label class="field">
          <span class="label-text">Password</span>
          <input
            v-model="password"
            type="password"
            autocomplete="new-password"
            minlength="8"
            :disabled="flow.busy"
            class="text-input"
          />
          <span v-if="touched && password && password.length < 8" class="hint-error">
            At least 8 characters.
          </span>
        </label>
        <label class="field">
          <span class="label-text">Confirm password</span>
          <input
            v-model="confirmPassword"
            type="password"
            autocomplete="new-password"
            :disabled="flow.busy"
            class="text-input"
          />
          <span v-if="passwordMismatch" class="hint-error">
            Passwords do not match.
          </span>
        </label>
      </div>

      <button
        type="submit"
        class="btn btn-primary btn-block"
        :disabled="flow.busy || !canSubmit"
      >
        <span v-if="flow.busy" class="spinner" aria-hidden="true"></span>
        <span v-else>Create account &amp; redeem</span>
      </button>
    </form>

    <div class="row">
      <button class="link-btn" type="button" @click="flow.goBackToChooseAuth()">
        ← Back
      </button>
      <button class="link-btn" type="button" @click="flow.goToSignIn()">
        Sign in instead →
      </button>
    </div>
  </section>
</template>

<style scoped>
.screen { display: flex; flex-direction: column; }

.title {
  font-family: 'Fraunces', Georgia, serif;
  font-size: clamp(1.6rem, 3vw, 2.1rem);
  letter-spacing: -0.02em;
  color: var(--coco-900);
  margin: 0 0 0.6rem;
  font-weight: 500;
  font-variation-settings: 'SOFT' 50, 'opsz' 96;
}
.lede {
  margin: 0 0 1.25rem;
  color: var(--ink-soft);
  line-height: 1.55;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin-bottom: 1rem;
}

.row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
}
@media (max-width: 480px) {
  .row-2 { grid-template-columns: 1fr; }
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.label-text {
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--ink);
}
.text-input {
  font: inherit;
  font-size: 1rem;
  padding: 0.7rem 0.9rem;
  border-radius: 10px;
  border: 1.5px solid var(--coco-200);
  background: var(--cream);
  color: var(--coco-900);
  transition: border-color 160ms var(--ease), box-shadow 160ms var(--ease);
}
.text-input:focus {
  outline: none;
  border-color: var(--lime-500);
  box-shadow: 0 0 0 4px var(--lime-100);
}
.hint-error {
  font-size: 0.78rem;
  color: #b14228;
}

.row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}
.link-btn {
  background: none;
  border: none;
  color: var(--coco-700);
  cursor: pointer;
  padding: 0.4rem 0;
  font: inherit;
  font-size: 0.88rem;
}
.link-btn:hover { color: var(--coco-900); }
</style>
